import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Filter, 
  X, 
  Calendar as CalendarIcon,
  RotateCcw 
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

export interface UserFilters {
  search: string;
  role: string;
  status: string;
  department: string;
  area: string;
  shift: string;
  experienceLevel: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

interface AdvancedFiltersProps {
  filters: UserFilters;
  onFiltersChange: (filters: UserFilters) => void;
  onReset: () => void;
}

const defaultFilters: UserFilters = {
  search: "",
  role: "all",
  status: "all",
  department: "all",
  area: "all",
  shift: "all",
  experienceLevel: "all",
  dateFrom: undefined,
  dateTo: undefined,
};

export function AdvancedFilters({ filters, onFiltersChange, onReset }: AdvancedFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === "search") return false;
    if (value === "all" || value === "" || value === undefined) return false;
    return true;
  }).length;

  const handleChange = (key: keyof UserFilters, value: string | Date | undefined) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleReset = () => {
    onFiltersChange(defaultFilters);
    onReset();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative">
          <Filter className="w-4 h-4 mr-2" />
          Filtros Avanzados
          {activeFiltersCount > 0 && (
            <Badge 
              variant="secondary" 
              className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[95vw] sm:w-[400px] max-w-[400px] p-4" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Filtros Avanzados</h4>
            <Button variant="ghost" size="sm" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          </div>

          <Separator />

          {/* Rol */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select
                value={filters.role}
                onValueChange={(value) => handleChange("role", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="operator">Operador</SelectItem>
                  <SelectItem value="quality">Calidad</SelectItem>
                  <SelectItem value="warehouse">Almacén</SelectItem>
                  <SelectItem value="maintenance">Mantenimiento</SelectItem>
                  <SelectItem value="viewer">Visor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Estado</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => handleChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Activos</SelectItem>
                  <SelectItem value="inactive">Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Departamento y Área */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Departamento</Label>
              <Select
                value={filters.department}
                onValueChange={(value) => handleChange("department", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="produccion">Producción</SelectItem>
                  <SelectItem value="calidad">Calidad</SelectItem>
                  <SelectItem value="almacen">Almacén</SelectItem>
                  <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                  <SelectItem value="administracion">Administración</SelectItem>
                  <SelectItem value="direccion">Dirección</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Área</Label>
              <Select
                value={filters.area}
                onValueChange={(value) => handleChange("area", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="corte_plotter">Corte Plotter</SelectItem>
                  <SelectItem value="laminador">Laminador</SelectItem>
                  <SelectItem value="taller">Taller</SelectItem>
                  <SelectItem value="generico">Genérico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Turno y Experiencia */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Turno</Label>
              <Select
                value={filters.shift}
                onValueChange={(value) => handleChange("shift", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="morning">Mañana</SelectItem>
                  <SelectItem value="afternoon">Tarde</SelectItem>
                  <SelectItem value="night">Noche</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Experiencia</Label>
              <Select
                value={filters.experienceLevel}
                onValueChange={(value) => handleChange("experienceLevel", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="junior">Junior</SelectItem>
                  <SelectItem value="mid">Intermedio</SelectItem>
                  <SelectItem value="senior">Senior</SelectItem>
                  <SelectItem value="expert">Experto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Rango de fechas */}
          <div className="space-y-2">
            <Label>Fecha de Alta</Label>
            <div className="grid grid-cols-2 gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !filters.dateFrom && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateFrom ? (
                      format(filters.dateFrom, "dd/MM/yyyy", { locale: es })
                    ) : (
                      "Desde"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateFrom}
                    onSelect={(date) => handleChange("dateFrom", date)}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !filters.dateTo && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateTo ? (
                      format(filters.dateTo, "dd/MM/yyyy", { locale: es })
                    ) : (
                      "Hasta"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateTo}
                    onSelect={(date) => handleChange("dateTo", date)}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Filtros activos */}
          {activeFiltersCount > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">Filtros activos</Label>
                <div className="flex flex-wrap gap-2">
                  {filters.role !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      Rol: {filters.role}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("role", "all")}
                      />
                    </Badge>
                  )}
                  {filters.status !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      Estado: {filters.status === "active" ? "Activo" : "Inactivo"}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("status", "all")}
                      />
                    </Badge>
                  )}
                  {filters.department !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      Depto: {filters.department}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("department", "all")}
                      />
                    </Badge>
                  )}
                  {filters.area !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      Área: {filters.area}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("area", "all")}
                      />
                    </Badge>
                  )}
                  {filters.shift !== "all" && (
                    <Badge variant="secondary" className="gap-1">
                      Turno: {filters.shift}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("shift", "all")}
                      />
                    </Badge>
                  )}
                  {filters.dateFrom && (
                    <Badge variant="secondary" className="gap-1">
                      Desde: {format(filters.dateFrom, "dd/MM/yy")}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("dateFrom", undefined)}
                      />
                    </Badge>
                  )}
                  {filters.dateTo && (
                    <Badge variant="secondary" className="gap-1">
                      Hasta: {format(filters.dateTo, "dd/MM/yy")}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleChange("dateTo", undefined)}
                      />
                    </Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { defaultFilters };





